import { useCallback, useMemo } from 'react';
import { parseUTCdateTime } from 'utils/dates';
import { useEval3Page } from './context';
import { useSavedEvaluations, useSelectEval } from './hooks';
import { evalDisplayName } from './utils';
import { EvalListTable, type EvalListRow } from './eval-list-table';
import styles from './saved-evals-page.module.scss';

/** The saved-evals table columns — Name / Saved by / Saved at (Workspace / Status deferred). */
const HEADERS = [
  { key: 'name', header: 'Name' },
  { key: 'savedBy', header: 'Saved by' },
  { key: 'savedAt', header: 'Saved at' },
] as const;

/**
 * The center body shown when the pinned "Saved Evaluations" folder is selected — a read-only,
 * package-wide table of the saved evaluations (mirrors {@link WorkspacePage}, but scoped to saved
 * evals across all workspaces via {@link useSavedEvaluations}). Prepended above the package-level
 * sections in the work area, exactly like the workspace-browse table.
 *
 * Each row routes to that saved eval via `useSelectEval(row.workspaceId, row.id)` — each saved row
 * carries its **own** `workspaceId` (unlike WorkspacePage's single folder id), passed through the
 * shared {@link EvalListTable}'s row-click. When there are no saved evals, a centered guidance block
 * explains what saving does (mock empty-state), instead of an empty table.
 *
 * @return {JSX.Element} The saved-evals table, its loading / error state, or the empty guidance block.
 */
export function SavedEvalsPage() {
  const { packageId } = useEval3Page();
  const selectEval = useSelectEval();
  const { data, isPending, isError, refetch } = useSavedEvaluations(packageId);

  const calcs = data?.calculations;

  const rows = useMemo<EvalListRow[]>(
    () =>
      (calcs ?? []).map((calc) => ({
        id: String(calc.id),
        name: evalDisplayName(calc.id, calc.description, calc.runByUser?.name),
        savedBy: calc.savedByUser?.name ?? '',
        savedAt: calc.savedAt ? parseUTCdateTime(calc.savedAt) : '',
      })),
    [calcs]
  );

  // Stable across renders so `EvalListTable`'s `columns` memo (keyed on `onRowClick`) — and the
  // DataTable's own column model — don't rebuild every render. Each saved eval carries its own
  // owning workspace, resolved from the loaded list at click time.
  const onRowClick = useCallback(
    (id: string) => {
      const calc = calcs?.find((c) => String(c.id) === id);
      if (calc) {
        selectEval(calc.workspaceId, calc.id);
      }
    },
    [calcs, selectEval]
  );

  // Settled with no saved evals → the guidance block, not an empty table (mock empty-state).
  if (!isPending && !isError && rows.length === 0) {
    return (
      <div className={styles.savedEvalsEmpty}>
        <h2 className={styles.savedEvalsEmptyTitle}>
          No saved evaluations yet
        </h2>
        <p className={styles.savedEvalsEmptyBody}>
          Save an evaluation to lock in an immutable snapshot of its settings,
          slot configuration, and value. Saved evaluations live here and are the
          only thing you can promote out of this package.
        </p>
      </div>
    );
  }

  const status = isError ? 'error' : isPending ? 'loading' : 'ready';

  return (
    <EvalListTable
      headers={HEADERS}
      rows={rows}
      status={status}
      errorMessage="Couldn’t load saved evaluations."
      onRetry={() => void refetch()}
      linkColumnKey="name"
      onRowClick={onRowClick}
    />
  );
}
